import { GetWebsiteEventsResponse, Primitive } from '@amzn/swo-unified-dashboard-service-lambda-js-client/lib/smswounifieddashboardservicelambda';
import { TimeMarker, Pipeline, Step, MergedPipeline } from '../types';
import { LAYOUT_CONSTANTS, primitiveColors, primitiveDisplayNames, TIME_CONSTANTS } from './constants';
import { StatusType as primitiveStatus, PrimitiveWorkflowStatus } from '../constants';
import { processMilestones, processMilestonesWithPipelineAlignment } from './milestone-processing';
import { calculateStepDuration } from './positioning';


/**
 * Formats a date into a human-readable time label with contextual information.
 * Shows just the time for today, "Yesterday" prefix for yesterday, or date + time for older dates.
 * 
 * @param date - The date to format
 * @returns A formatted time string (e.g., "14:30", "Yesterday 09:15", "Jan 5 16:45")
 */
export const formatTimeLabel = (date: Date): string => {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  
  const timeString = date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
  
  if (isToday) {
    return timeString;
  } else if (isYesterday) {
    return `Yesterday ${timeString}`;
  } else {
    return `${date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    })} ${timeString}`;
  }
};
/**
 * Formats a date into a complete date-time string with full precision.
 * 
 * @param date - The date to format
 * @returns A full date-time string (e.g., "Jan 5, 2024, 14:30:45")
 */
export const getFullDateTimeLabel = (date: Date): string => {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

/**
 * Calculates the time range for displaying milestones with appropriate padding.
 * Adds 10% padding on each side of the timeline, with a minimum of 1 hour padding.
 * 
 * @param milestones - Array of milestone objects with timestamp properties
 * @returns Object containing start and end dates for the padded time range
 */
export const calculateTimeRange = (milestones: any[]) => {
  if (!milestones.length) {
    const now = new Date();
    return { start: now, end: now };
  }
  // Convert timestamps to Date objects and sort
  const milestoneDates = milestones.map((m: any) => ({
    ...m,
    date: new Date(m.timestamp || -1)
  })).sort((a: any, b: any) => a.date.getTime() - b.date.getTime());
  const startTime = new Date(milestoneDates[0].date);
  const endTime = new Date(milestoneDates[milestoneDates.length - 1].date);
  
  // Add padding (10% of total duration on each side, minimum 1 hour)
  const totalDuration = endTime.getTime() - startTime.getTime();
  const padding = Math.max(
    totalDuration * LAYOUT_CONSTANTS.TIME_RANGE_PADDING_PERCENTAGE, 
    LAYOUT_CONSTANTS.MINIMUM_TIME_PADDING
  );
  
  return {
    start: new Date(startTime.getTime() - padding),
    end: new Date(endTime.getTime() + padding)
  };
};
/**
 * Helper function to detect time overlap between two pipelines.
 * Checks if the time ranges of two pipelines overlap within an optional threshold.
 * 
 * @param pipeline1 - First pipeline to compare
 * @param pipeline2 - Second pipeline to compare
 * @param overlapThresholdMs - Optional threshold in milliseconds for overlap detection
 * @returns True if pipelines overlap, false otherwise
 */
const pipelinesOverlap = (pipeline1: Pipeline, pipeline2: Pipeline, overlapThresholdMs: number = 0): boolean => {
  if (!pipeline1.startTime || !pipeline1.endTime || !pipeline2.startTime || !pipeline2.endTime) {
    return false;
  }
  
  const p1Start = pipeline1.startTime.getTime();
  const p1End = pipeline1.endTime.getTime();
  const p2Start = pipeline2.startTime.getTime();
  const p2End = pipeline2.endTime.getTime();
  
  // Check if there's any time overlap (with optional threshold)
  return (p1Start <= p2End + overlapThresholdMs) && (p2Start <= p1End + overlapThresholdMs);
};
/**
 * Helper function to merge overlapping pipelines into consolidated groups.
 * Groups pipelines that have overlapping time ranges and merges them into single pipelines.
 * 
 * @param pipelines - Array of pipelines to process for overlaps
 * @returns Array of pipelines with overlapping ones merged
 */
const mergeOverlappingPipelines = (pipelines: Pipeline[]): Pipeline[] => {
  if (pipelines.length <= 1) {
    return pipelines;
  }
  const merged: Pipeline[] = [];
  const processed = new Set<string>();
  
  for (let i = 0; i < pipelines.length; i++) {
    if (processed.has(pipelines[i].id)) {
      continue;
    }
    
    const currentPipeline = pipelines[i];
    const overlappingPipelines: Pipeline[] = [currentPipeline];
    processed.add(currentPipeline.id);
    
    // Find all pipelines that overlap with the current one
    for (let j = i + 1; j < pipelines.length; j++) {
      if (processed.has(pipelines[j].id)) {
        continue;
      }
      
      const candidatePipeline = pipelines[j];
      
      // Check if this pipeline overlaps with any pipeline in the current group
      const overlapsWithGroup = overlappingPipelines.some(groupPipeline => 
        pipelinesOverlap(groupPipeline, candidatePipeline)
      );
      
      if (overlapsWithGroup) {
        overlappingPipelines.push(candidatePipeline);
        processed.add(candidatePipeline.id);
      }
    }
    
    if (overlappingPipelines.length > 1) {
      // Merge the overlapping pipelines
      const mergedPipeline = createMergedPipeline(overlappingPipelines);
      merged.push(mergedPipeline);
    } else {
      // No overlap, keep the original pipeline
      merged.push(currentPipeline);
    }
  }
  
  return merged;
};
/**
 * Helper function to create a merged pipeline from multiple overlapping pipelines.
 * Combines steps from all input pipelines into a single merged pipeline with proper lane assignment.
 * 
 * @param pipelines - Array of pipelines to merge
 * @returns A single merged pipeline containing all steps from input pipelines
 */
const createMergedPipeline = (pipelines: Pipeline[]): MergedPipeline => {
  // Calculate the overall time range for the merged pipeline
  const allStartTimes = pipelines
    .filter(p => p.startTime)
    .map(p => p.startTime!.getTime());
  const allEndTimes = pipelines
    .filter(p => p.endTime)
    .map(p => p.endTime!.getTime());
  
  const mergedStartTime = new Date(Math.min(...allStartTimes));
  const mergedEndTime = new Date(Math.max(...allEndTimes));
  
  // Collect all steps from all pipelines and arrange them in lanes
  const allSteps: Step[] = [];
  let currentLane = 0;
  
  pipelines.forEach((pipeline, pipelineIndex) => {
    // Sort steps within each pipeline chronologically
    const sortedSteps = [...pipeline.steps].sort((a, b) => {
      if (a.startTime && b.startTime) {
        return a.startTime.getTime() - b.startTime.getTime();
      }
      return 0;
    });
    
    sortedSteps.forEach((step, stepIndex) => {
      // Assign each step to a new lane to avoid overlap
      const mergedStep: Step = {
        ...step,
        lane: currentLane,
        // Add metadata to track which original pipeline this step came from
        metrics: {
          ...step.metrics,
          'Original Pipeline': pipeline.name,
          'Merged Lane': `${currentLane + 1}`
        }
      };
      
      allSteps.push(mergedStep);
      currentLane++;
    });
  });
  
  // Create merged pipeline name
  const traceIds = pipelines.map(p => p.id.replace('trace-', '')).join(', ');
  const mergedName = `Merged Traces (${traceIds})`;
  
  // Use the earliest pipeline's date
  const earliestPipeline = pipelines.reduce((earliest, current) => 
    current.traceStartTime < earliest.traceStartTime ? current : earliest
  );
  
  const mergedPipeline: MergedPipeline = {
    id: `merged-${pipelines.map(p => p.id).join('-')}`,
    name: mergedName,
    date: earliestPipeline.date,
    steps: allSteps,
    traceStartTime: Math.min(...pipelines.map(p => p.traceStartTime)),
    startTime: mergedStartTime,
    endTime: mergedEndTime,
    startPosition: 0, // Will be recalculated later
    endPosition: 0,   // Will be recalculated later
    width: 0,         // Will be recalculated later
    mergedTraceIds: pipelines.map(p => p.id.replace('trace-', '')),
    originalPipelines: pipelines
  };
  
  return mergedPipeline;
};
/**
 * Processes primitives (workflow steps) into horizontal pipeline layout.
 * Groups primitives by trace ID, creates pipeline objects with steps positioned
 * based on their timing, and handles merging of overlapping pipelines.
 * 
 * @param primitives - Array of primitive objects with traceId, startTime, endTime properties
 * @param paddedStart - Start date of the padded time range (unused but kept for compatibility)
 * @param paddedEnd - End date of the padded time range (unused but kept for compatibility)
 * @returns Array of processed pipelines with positioned steps
 */
export const processPrimitivesHorizontal = (
  primitives: Primitive[]
): Pipeline[] => {
  if (!primitives.length) {
    return [];
  }
  // Group primitives by traceId
  const primitivesByTrace = primitives.reduce((acc: any, primitive: any) => {
    const traceId = primitive.traceId;
    if (!acc[traceId]) {
      acc[traceId] = [];
    }
    acc[traceId].push(primitive);
    return acc;
  }, {});
  const processedPipelines: Pipeline[] = [];
  let pipelineIndex = 0;
  
  // Create a pipeline for each trace
  Object.entries(primitivesByTrace).forEach(([traceId, tracePrimitives]) => {
    const typedTracePrimitives = tracePrimitives as any[];
    
    // Sort primitives within the trace by start time
    const sortedPrimitives = typedTracePrimitives.sort((a: any, b: any) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    const processedPrimitives = sortedPrimitives.map((primitive: any) => {
      const startTime = new Date(primitive.startTime);
      let endTime = primitive.endTime ? new Date(primitive.endTime) : null;
      let status = primitiveStatus.COMPLETED;
      if(typeof primitive.metadata?.['state'] === 'string' && primitive.metadata['state'].toLowerCase() === PrimitiveWorkflowStatus.FAILED.toLowerCase()){
        status = primitiveStatus.ERROR;
      }
      
      /* 
        If endTime is missing, null, undefined, or invalid, or if primitive stattus is running set it to current time.
      */
      if (!endTime || isNaN(endTime.getTime()) || 
      (typeof primitive.metadata?.['state'] === 'string' && primitive.metadata['state'].toLowerCase() === PrimitiveWorkflowStatus.RUNNING.toLowerCase()) ) {
        status = primitiveStatus.PENDING
        endTime = new Date();
      }
      
      return {
        ...primitive,
        startTime,
        status,
        endTime
      };
    });
    // Calculate pipeline-specific time range
    const pipelineStartTimes = processedPrimitives.map((p: any) => new Date(p.startTime).getTime());
    const pipelineEndTimes = processedPrimitives.map((p: any) => new Date(p.endTime).getTime());
    const pipelineStart = new Date(Math.min(...pipelineStartTimes));
    const pipelineEnd = new Date(Math.max(...pipelineEndTimes));
    // Calculate total trace duration for proportional sizing within the pipeline slot
    const traceDuration = pipelineEnd.getTime() - pipelineStart.getTime();
    
    // For horizontal layout, each pipeline gets equal horizontal space (will be recalculated after merging)
    const pipelineWidth = 100 / Object.keys(primitivesByTrace).length;
    const steps: Step[] = processedPrimitives.map((primitive: any, stepIndex: number) => {
      const startTime = new Date(primitive.startTime);
      const endTime = new Date(primitive.endTime);
      
      // Calculate step position and width based on its timing within the trace
      const stepStartOffset = startTime.getTime() - pipelineStart.getTime();
      const stepDuration = endTime.getTime() - startTime.getTime();
      
      // Store relative positioning information that will be used by the rendering logic
      // The rendering logic will calculate the actual pixel positions based on these values
      const stepStartRatio = traceDuration > 0 ? stepStartOffset / traceDuration : 0;
      const stepDurationRatio = traceDuration > 0 ? stepDuration / traceDuration : 0;
  
      const displayName = (primitive.name && primitiveDisplayNames[primitive.name]) || primitive.name;
      return {
        name: displayName,
        start: stepStartRatio, // Store as ratio (0-1) for rendering logic to use
        width: stepDurationRatio, // Store as ratio (0-1) for rendering logic to use
        color: (displayName && primitiveColors[displayName]) || '#2196f3',
        status: primitive.status,
        lane: stepIndex,
        startTime: startTime,
        triggerType: primitive.metadata?.['triggertype'],
        endTime: endTime,
        metrics: {
          'Duration': calculateStepDuration(startTime.getTime(), endTime.getTime()),
          'Start Time': getFullDateTimeLabel(startTime),
          'Trigger Type': primitive.metadata?.['triggertype'],
          ...(primitive.status === primitiveStatus.PENDING ? {} : {'Last Update Time': getFullDateTimeLabel(endTime)}),
          ...(primitive.metadata?.['workflowIds'] ? {'workflowIds': primitive.metadata?.['workflowIds']} : {})
        },
        metadata: {
          ...primitive.metadata
        }
      };
    });

    // Calculate the earliest start time for the trace
    const traceStartTime = Math.min(...sortedPrimitives.map((p: any) => new Date(p.startTime).getTime()));
    const traceStartDate = new Date(traceStartTime);

    processedPipelines.push({
      id: `trace-${traceId}`,
      name: `Trace ${traceId}`,
      date: traceStartDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }),
      steps,
      traceStartTime,
      startTime: pipelineStart,
      endTime: pipelineEnd,
      // Add positioning information for the pipeline header (equal distribution)
      startPosition: pipelineIndex * pipelineWidth,
      endPosition: (pipelineIndex + 1) * pipelineWidth,
      width: pipelineWidth
    });

    pipelineIndex++;
  });

  // Sort pipelines by their start time
  processedPipelines.sort((a, b) => {
    return a.traceStartTime - b.traceStartTime;
  });

  // Merge overlapping pipelines
  const mergedPipelines = mergeOverlappingPipelines(processedPipelines);

  // Recalculate positioning after merging
  mergedPipelines.forEach((pipeline, index) => {
    const pipelineWidth = 100 / mergedPipelines.length;
    pipeline.startPosition = index * pipelineWidth;
    pipeline.endPosition = (index + 1) * pipelineWidth;
    pipeline.width = pipelineWidth;
    
    // Update step positions within the pipeline
    pipeline.steps.forEach((step) => {
      // Recalculate step positions based on new pipeline position
      const stepRelativeStart = step.start - (pipeline.startPosition || 0);
      const stepRelativePosition = stepRelativeStart / (pipeline.width || pipelineWidth);
      step.start = index * pipelineWidth + stepRelativePosition * pipelineWidth;
    });
  });

  return mergedPipelines;
};

/**
 * Generates time markers aligned with pipeline column positions.
 * Creates markers at the center of each pipeline column for better visual alignment.
 * 
 * @param pipelines - Array of pipeline objects to generate markers for
 * @returns Array of time markers positioned at pipeline column centers
 */
export const generatePipelineAlignedTimeMarkers = (pipelines: Pipeline[]): TimeMarker[] => {
  if (!pipelines.length) {
    return [];
  }
  const markers: TimeMarker[] = [];
  
  // Create markers at the center of each pipeline column
  pipelines.forEach((pipeline, index) => {
    const pipelineWidth = 100 / pipelines.length;
    const centerPosition = (index * pipelineWidth) + (pipelineWidth / 2);
    
    // Use the pipeline's start time as the representative time for this column
    const representativeTime = pipeline.startTime || new Date();
    
    markers.push({
      label: formatTimeLabel(representativeTime),
      position: centerPosition,
      fullDateTime: representativeTime
    });
  });

  return markers.sort((a, b) => a.position - b.position);
};

/**
 * Main function to process timeline data including milestones and primitives.
 * Orchestrates the processing of both milestones and primitives into a format
 * suitable for timeline visualization with proper alignment and positioning.
 * 
 * @param timelineData - Optional timeline data containing milestones and primitives
 * @returns Object containing processed timeline components (markers, milestones, pipelines, time range)
 */
export const processTimelineData = (timelineData?: GetWebsiteEventsResponse) => {
  const milestones =  timelineData?.milestones || [];
  const primitives = timelineData?.primitives || [];
  if (!milestones.length) {
    return { 
      timeMarkers: [], 
      processedMilestones: [], 
      processedPipelines: [],
      timeRange: { start: new Date(), end: new Date() } 
    };
  }
  const processedPipelines = processPrimitivesHorizontal(primitives);

  const timeRange = processedPipelines.length > 0 
    ? {
        start: new Date(Math.min(...processedPipelines.map(p => p.startTime!.getTime()))),
        end: new Date(Math.max(...processedPipelines.map(p => p.endTime!.getTime())))
      }
    : calculateTimeRange(milestones);
  
  // Use pipeline-aligned milestone processing if pipelines exist, otherwise fall back to original
  const processedMilestones = processedPipelines.length > 0 
    ? processMilestonesWithPipelineAlignment(milestones, processedPipelines)
    : processMilestones(milestones, timeRange.start, timeRange.end);
  
  // Generate time markers aligned with pipeline boundaries
  const timeMarkers = generatePipelineAlignedTimeMarkers(processedPipelines);
  return {
    timeMarkers,
    processedMilestones,
    processedPipelines,
    timeRange
  };
};
